(() => {
  'use strict';

  if (window.LalaLaunchState?.version === '1') {
    window.LalaLaunchState.sync('duplicate-load');
    return;
  }

  const MAX_TIMER_DELAY = 2147483647;
  let timer = null;
  let started = false;

  const finiteTimestamp = (value) => {
    const timestamp = Number(value);
    return Number.isFinite(timestamp) ? timestamp : null;
  };

  const setHidden = (element, hidden) => {
    element.hidden = hidden;
    if (hidden) element.setAttribute('aria-hidden', 'true');
    else element.removeAttribute('aria-hidden');
  };

  const syncHeroes = (now) => {
    document.querySelectorAll('[data-lala-dose-action][data-lala-preorder-expires-at]').forEach((action) => {
      const expiresAt = finiteTimestamp(action.dataset.lalaPreorderExpiresAt);
      const preorderActive = expiresAt !== null && now < expiresAt;
      const slide = action.closest('[data-lala-dose-restock-slide]');
      const label = action.querySelector('[data-lala-dose-action-label]');
      const heading = slide?.querySelector('[data-lala-dose-heading]');
      if (label) label.textContent = preorderActive ? action.dataset.lalaPreorderLabel : action.dataset.lalaShopLabel;
      if (heading) heading.textContent = preorderActive ? heading.dataset.lalaPreorderHeading : heading.dataset.lalaShopHeading;
    });
  };

  const syncJarGrids = (now) => {
    document.querySelectorAll('[data-lala-jar-grid][data-lala-preorder-expires-at]').forEach((grid) => {
      const expiresAt = finiteTimestamp(grid.dataset.lalaPreorderExpiresAt);
      const preorderActive = expiresAt !== null && now < expiresAt;
      grid.dataset.lalaPreorderActive = String(preorderActive);
      grid.querySelectorAll('[data-lala-copy="preorder"]').forEach((element) => { element.hidden = !preorderActive; });
      grid.querySelectorAll('[data-lala-copy="shop"]').forEach((element) => { element.hidden = preorderActive; });
      grid.querySelectorAll('[data-lala-jar-link]').forEach((link) => {
        const label = preorderActive ? link.dataset.preorderAriaLabel : link.dataset.shopAriaLabel;
        if (label) link.setAttribute('aria-label', label);
      });
    });
  };

  const syncBundleLaunches = (now) => {
    document.querySelectorAll('.lala-set__launch[data-lala-preorder-expires-at]').forEach((launch) => {
      const expiresAt = finiteTimestamp(launch.dataset.lalaPreorderExpiresAt);
      setHidden(launch, expiresAt === null || now >= expiresAt);
    });
  };

  const syncLaunchNotes = (now) => {
    document.querySelectorAll('[data-lala-launch-note][data-lala-evergreen-at]').forEach((note) => {
      const evergreenAt = finiteTimestamp(note.dataset.lalaEvergreenAt);
      const evergreen = evergreenAt === null || now >= evergreenAt;
      const launchDayAt = finiteTimestamp(note.dataset.lalaLaunchDayAt);
      const launchDay = launchDayAt !== null && now >= launchDayAt;
      const label = evergreen ? note.dataset.lalaEvergreenLabel
        : launchDay ? (note.dataset.lalaLaunchDayLabel || note.dataset.lalaLaunchLabel)
          : note.dataset.lalaLaunchLabel;
      if (!label) return;
      note.dataset.lalaLaunchEvergreen = String(evergreen);
      note.querySelector('[data-lala-launch-note-link]')?.setAttribute('aria-label', label);
      const accessibleLabel = note.querySelector('[data-lala-launch-note-accessible]');
      if (accessibleLabel) accessibleLabel.textContent = label;
      note.querySelectorAll('[data-lala-launch-note-copy]').forEach((copy) => { copy.textContent = label; });
    });
  };

  const expireBusyPreorderMarkup = (preorder, now) => {
    const expiresAt = finiteTimestamp(preorder.dataset.lalaPreorderExpiresAt);
    if (expiresAt === null || now < expiresAt) return;
    setHidden(preorder, true);
    const property = preorder.querySelector('[data-lala-preorder-property]');
    if (property) property.disabled = true;
    preorder.closest('form')?.removeAttribute('data-lala-preorder-active');
  };

  const syncProductPreorders = (now) => {
    document.querySelectorAll('[data-lala-jar-preorder]').forEach((preorder) => expireBusyPreorderMarkup(preorder, now));
    document.querySelectorAll('product-variants').forEach((variants) => {
      const form = variants.productForm;
      if (!form?.querySelector('[data-lala-jar-preorder]')) return;
      if (form.getAttribute('aria-busy') === 'true') return;
      if (typeof variants.syncLalaJarPreorderState === 'function') variants.syncLalaJarPreorderState();
      else if (typeof variants.updateBuyButtons === 'function') variants.updateBuyButtons();
    });
  };

  const futureCutoffs = (now) => {
    const values = [];
    document.querySelectorAll('[data-lala-preorder-expires-at], [data-lala-launch-day-at], [data-lala-evergreen-at]').forEach((element) => {
      [element.dataset.lalaPreorderExpiresAt, element.dataset.lalaLaunchDayAt, element.dataset.lalaEvergreenAt].forEach((raw) => {
        const cutoff = finiteTimestamp(raw);
        if (cutoff !== null && cutoff > now) values.push(cutoff);
      });
    });
    return values;
  };

  const schedule = (now) => {
    if (timer !== null) {
      window.clearTimeout(timer);
      timer = null;
    }
    const nextCutoff = Math.min(...futureCutoffs(now));
    if (!Number.isFinite(nextCutoff)) return;
    const delay = Math.min(Math.max(nextCutoff - now + 25, 25), MAX_TIMER_DELAY);
    timer = window.setTimeout(() => {
      timer = null;
      sync('timer');
    }, delay);
  };

  const sync = () => {
    const now = Date.now();
    syncHeroes(now);
    syncJarGrids(now);
    syncBundleLaunches(now);
    syncLaunchNotes(now);
    syncProductPreorders(now);
    schedule(now);
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') sync('visibilitychange');
  };
  const onPageShow = () => sync('pageshow');
  const onFocus = () => sync('focus');
  const onSectionLoad = () => sync('shopify:section:load');

  const start = () => {
    if (started) return;
    started = true;
    document.addEventListener('visibilitychange', onVisibilityChange);
    document.addEventListener('shopify:section:load', onSectionLoad);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('focus', onFocus);
    sync('initial');
  };

  const destroy = () => {
    if (!started) return;
    started = false;
    if (timer !== null) window.clearTimeout(timer);
    timer = null;
    document.removeEventListener('visibilitychange', onVisibilityChange);
    document.removeEventListener('shopify:section:load', onSectionLoad);
    window.removeEventListener('pageshow', onPageShow);
    window.removeEventListener('focus', onFocus);
  };

  window.LalaLaunchState = Object.freeze({ version: '1', sync, start, destroy });
  start();
})();
